// src/pages/QbusAutomations/ExecutionLogDialog.jsx
// Modal showing execution history with step-by-step details
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Collapse,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { qbusApi } from '../../services/apiClient';

const statusColors = {
  running: 'warning',
  completed: 'success',
  failed: 'error',
  cancelled: 'default'
};

const ExecutionLogDialog = ({ automationId, automationName, open, onClose }) => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!open || !automationId) return;
    setLoading(true);
    qbusApi.getAutomationExecutions(automationId, { limit: 20 })
      .then(res => setExecutions(res.data.executions || []))
      .catch(err => console.error('Error fetching executions:', err))
      .finally(() => setLoading(false));
  }, [open, automationId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon />
          <Typography variant="h6">Execution Log: {automationName}</Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : executions.length === 0 ? (
          <Alert severity="info">No executions recorded yet.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>Started</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Test?</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {executions.map((exec) => {
                  const duration = exec.finished_at
                    ? `${((new Date(exec.finished_at) - new Date(exec.started_at)) / 1000).toFixed(1)}s`
                    : 'running...';

                  return (
                    <React.Fragment key={exec.id}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpandedId(expandedId === exec.id ? null : exec.id)}>
                        <TableCell>
                          <IconButton size="small" sx={{ transform: expandedId === exec.id ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>
                            <ExpandIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(exec.started_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>{duration}</TableCell>
                        <TableCell>
                          <Chip label={exec.status} size="small" color={statusColors[exec.status] || 'default'} />
                        </TableCell>
                        <TableCell>
                          {exec.is_test && <Chip label="Test" size="small" variant="outlined" />}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="error" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                            {exec.error_message || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Expanded step details */}
                      <TableRow>
                        <TableCell colSpan={6} sx={{ p: 0, borderBottom: expandedId === exec.id ? undefined : 'none' }}>
                          <Collapse in={expandedId === exec.id}>
                            <Paper sx={{ m: 1, p: 2, bgcolor: 'action.hover' }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>Execution Steps</Typography>
                              {(exec.execution_log || []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No steps recorded</Typography>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Block</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>Output</TableCell>
                                      <TableCell>Error</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(exec.execution_log || []).map((step, i) => (
                                      <TableRow key={i}>
                                        <TableCell>
                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {step.label || step.blockType}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Chip label={step.status} size="small" color={statusColors[step.status] || 'default'} />
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                            {step.output ? JSON.stringify(step.output) : '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption" color="error">
                                            {step.error || '-'}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Paper>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExecutionLogDialog;
